/*    
<summary>
   This class component is all about Managing user data functionality.
   Developer:Mohammad Saquib Khan, Created Date:29-March-2024
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, makeObservable, observable } from "mobx";
import { toast } from "react-hot-toast";
import URLConstants from "../../constants/url-constants";
import { getUserIdFromToken } from "../../helpers/localstorage-helper";
import { ISendMail } from "../../models/forms/IAddUser";
import {
  IResetPassword,
  IUpdateLoggedInUserPassword,
  IUpdateStorePassword
} from "../../models/forms/IChangePassword";
import { IObservableInitialState } from "../../models/ICommon";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { IUserState } from "../../models/state/IUserState";
import { formatMessage } from "../../translations/format-message";
import * as baseService from "../service/base-service";

export class UserStore implements IUserState, ICommonState {
  inProgress = false;
  error = "";
  tenantAccessId = getUserIdFromToken();
  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  user: any | undefined = undefined;
  userState = { ...this.initialStateValue };
  changePasswordState = { ...this.initialStateValue };
  activateUserAccountState = { ...this.initialStateValue };
  addUpdateUserState = { ...this.initialStateValue };
  deleteUserState = { ...this.initialStateValue };
  resendActivationLinkState = { ...this.initialStateValue };
  verifyPasswordResetAccountState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      user: observable,
      userState: observable,
      changePasswordState: observable,
      addUpdateUserState: observable,
      deleteUserState: observable,
      activateUserAccountState: observable,
      resendActivationLinkState: observable,
      verifyPasswordResetAccountState: observable,
      UpdatePasswordService: action,
      ActivateUserService: action,
      VerifyUserByUserIdService: action,
      ResetPasswordService: action,
      UpdateLoggedInUserPasswordService: action,
      UpdateHeadquarterPasswordService: action,
      SendUserVerifyPasswordResetLink: action,
      ResendAccountActivateLinkService: action,
      resetAccountActivationLink: action,
      resetVerifyPasswordLinkState: action,
      resetChangePassword: action,
      reset: action,
      resetUserDetail: action,
      resetDeleteUser: action,
      resetAccountActivationState: action,
    });
  }

  /**
   * This function is used to Resend User Activation Link by calling an API.
   * @param data : User Data
   * @returns
   */
  ResendAccountActivateLinkService = (data: ISendMail) => {
    this.resendActivationLinkState.inProgress = true;
    let url = URLConstants.ResendLink;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.resendActivationLinkState.error = response.data.Message;
        } else this.resendActivationLinkState.success = true;
      })
      .catch((err: string) => {
        this.resendActivationLinkState.error = err;
      })
      .finally(
        action(() => {
          this.resendActivationLinkState.inProgress = false;
        })
      );
  };

  SendUserVerifyPasswordResetLink = (data: any) => {
    this.verifyPasswordResetAccountState.inProgress = true;
    let url = URLConstants.PasswordResetLink;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.verifyPasswordResetAccountState.error = response.data.Message;
        } else this.verifyPasswordResetAccountState.success = true;
      })
      .catch((err: string) => {
        this.verifyPasswordResetAccountState.error = err;
      })
      .finally(
        action(() => {
          this.verifyPasswordResetAccountState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Activate User Account by calling an API.
   * @param id : The User Identifier
   * @param data
   * @returns
   */
  ActivateUserService = (
    userId: number,
    email: string,
    accountStatus: number
  ) => {
    this.activateUserAccountState.inProgress = true;
    let url = URLConstants.ActivateAccount;
    let obj = {
      UserId: userId,
      Email: email,
      AccountStatus: accountStatus,
    };
    return baseService
      .postRequest(url, obj)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activateUserAccountState.error = response.data.Message;
        } else {
          this.activateUserAccountState.success = true;
          this.activateUserAccountState.error = response.data.Message;
        }
      })
      .catch((err: string) => {
        this.activateUserAccountState.error = err;
      })
      .finally(
        action(() => {
          this.activateUserAccountState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Delete User by calling an API.
   * @param id : The User Identifier
   * @returns
   */
  DeleteUserService = (id: number) => {
    this.deleteUserState.inProgress = true;
    let url = URLConstants.DeleteUser + "/" + id;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteUserState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteUserState.success = true;
      })
      .catch((err: string) => {
        this.deleteUserState.error = err;
      })
      .finally(
        action(() => {
          this.deleteUserState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Get User Details by Id by calling an API.
   * @param id : The User Identifier
   * @returns
   */
  public VerifyUserByUserIdService = (id: number) => {
    this.userState.inProgress = true;
    let url = `${URLConstants.AuthorizeAccount}/${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.userState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.user = data;
        }
      })
      .catch((err: string) => {
        this.userState.error = err;
      })
      .finally(
        action(() => {
          this.userState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Change Logged in User Password by calling an API & sending new Password details.
   * @param data : Password data
   * @returns
   */
  UpdateLoggedInUserPasswordService = (data: IUpdateLoggedInUserPassword) => {
    this.changePasswordState.inProgress = true;
    let url = URLConstants.ChangePassword;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.changePasswordState.error = response.data.Message;
        } else this.changePasswordState.success = true;
      })
      .catch((err: any) => {
        this.changePasswordState.error = err;
      })
      .finally(
        action(() => {
          this.changePasswordState.inProgress = false;
        })
      );
  };

  /**
   * This function is used by Tenant to update its user password calling an APi & sending the password details
   * @param data : New Password Details
   * @returns
   */
  UpdatePasswordService = (data: IUpdateStorePassword) => {
    this.changePasswordState.inProgress = true;
    let url: string = URLConstants.UpdateStorePassword;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.changePasswordState.error = response.data.Message;
        } else this.changePasswordState.success = true;
      })
      .catch((err: any) => {
        this.changePasswordState.error = err;
      })
      .finally(
        action(() => {
          this.changePasswordState.inProgress = false;
        })
      );
  };

  /**
   * This function is used by Tenant to update its user password calling an APi & sending the password details
   * @param data : New Password Details
   * @returns
   */
  UpdateHeadquarterPasswordService = (data: any) => {
    this.changePasswordState.inProgress = true;
    let url: string = URLConstants.UpdateHeadquarterPassword;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.changePasswordState.error = response.data.Message;
        } else this.changePasswordState.success = true;
      })
      .catch((err: any) => {
        this.changePasswordState.error = err;
      })
      .finally(
        action(() => {
          this.changePasswordState.inProgress = false;
        })
      );
  };

  /**
   * This function is used by Tenant to update its user password calling an APi & sending the password details
   * @param data : New Password Details
   * @returns
   */
  ResetPasswordService = (data: IResetPassword) => {
    this.changePasswordState.inProgress = true;
    let url: string = URLConstants.ResetPassword;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.changePasswordState.error = response.data.Message;
        } else this.changePasswordState.success = true;
      })
      .catch((err: any) => {
        this.changePasswordState.error = err;
      })
      .finally(
        action(() => {
          this.changePasswordState.inProgress = false;
        })
      );
  };

  /*This function is used to reset all changePassword observables to their initial values.
   */
  resetChangePassword = () => {
    this.changePasswordState = { ...this.initialStateValue };
  };

  /**
    This function is used to reset all user observables to their initial values.  
    */
  resetAccountActivationLink = () => {
    this.resendActivationLinkState = { ...this.initialStateValue };
  };

  resetUserDetail = () => {
    this.user = undefined;
    this.userState = { ...this.initialStateValue };
  };

  resetDeleteUser = () => {
    this.deleteUserState = { ...this.initialStateValue };
  };

  resetAccountActivationState = () => {
    this.activateUserAccountState = { ...this.initialStateValue };
  };

  resetVerifyPasswordLinkState = () => {
    this.verifyPasswordResetAccountState = { ...this.initialStateValue };
  };

  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateUserState.success = false;
    this.addUpdateUserState.error = "";
    this.deleteUserState = { ...this.initialStateValue };
    this.addUpdateUserState.inProgress = false;
    this.userState = { ...this.initialStateValue };
    this.verifyPasswordResetAccountState = { ...this.initialStateValue };
  };
}

export default new UserStore();
